'use client';
import AddToCuratedList from '@c/admin/AddToCuratedList';
import ProductAdminControls from '@c/admin/ProductAdminControls';
import ProductDetailsBanner from '@c/banners/ProductDetailsBanner';
import ShareProductModal from '@c/modals/ShareProductModal';
import ProductCouponDisplay from '@c/ProductCouponDisplay';
import { ProductDetails } from '@c/ProductDetails';
import SellerVacationDisplay from '@c/SellerVacationDisplay';
import { useQuery } from '@tanstack/react-query';
import Breadcrumb, { BreadcrumbItem } from '@ui/Breadcrumb';
import { viewContent } from '@util/analytics';
import { getRelatedProducts } from '@util/firestore/products';
import { trackUserInteraction } from '@util/firestore/recommendations';
import { formatTimestamp } from '@util/index';
import { useAuth } from 'context/AuthContext';
import useMyFeed from 'features/homepage/hooks/useMyFeed';
import {
  injectBreadcrumbScript,
  injectProductScript,
} from 'features/schemaMarkups';
import { ProductDocument, Variation } from 'models/product';
import { PublicUserDocument } from 'models/user';
import { Suspense, useEffect } from 'react';
import { Montserrat } from 'next/font/google';
import FitmentInformationBadges from './FitmentInformationBadges';
import ProductInfoTabs from './ProductInfoTabs';
import PageSectionHeader from '@ui/PageSectionHeader';
import { Carousel } from '@c/index';
import MXSmallProductCard from '@c/MXSmallProductCard';
import useSuggestedProducts from '@util/hooks/useSuggestedProducts';
import ConfirmFitment from './ConfirmFitment';
import InformationBadge from '@c/InformationBadge';
import { getReviewsByUid } from '@util/firestore/reviews';
import ProductMeetTheSeller from '@c/ProductMeetTheSeller';
import useBuyerActionButtons from 'features/shop/hooks/useBuyerActionButtons';

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
});

export default function ProductDetailPage(props: {
  product: ProductDocument;
  seller: PublicUserDocument | null;
  breadcrumbs: BreadcrumbItem[];
  isPreview?: boolean;
  schemaRatings: {
    product_rating: string;
    product_reviews: string;
  };
  fitment: Map<string, Map<string, { years: number[]; rideType: string }>>;
}) {
  const { product, breadcrumbs, seller, schemaRatings, fitment } = props;
  const { userDoc } = useAuth();
  const { data } = useQuery({
    queryKey: ['relatedProducts', product.id],
    queryFn: () => getRelatedProducts(product),
    enabled: !!product.id,
  });
  const { basedOnActivity } = useSuggestedProducts();
  const { selectedVariations, setErrors, onSelection } =
    useBuyerActionButtons(product);
  const relatedProducts = data?.results;
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (typeof (window as any).fbq === 'function') {
        viewContent(product, 'product', userDoc?.uid ?? '');
        if (userDoc?.uid) {
          trackUserInteraction({
            uid: userDoc.uid,
            pid: product.id,
            interaction: 'view',
          });
        }
        clearInterval(intervalId); // Stop polling when fbq is available
      }
    }, 1000); // Check every second

    return () => clearInterval(intervalId); // Clean up interval on unmount
  }, [product, userDoc?.uid]);

  const { data: reviews } = useQuery({
    queryKey: ['reviews'],
    queryFn: () =>
      getReviewsByUid({
        uid: product.seller_id ?? '',
        reviewsPerPage: 10,
      }),
    enabled: !!product.seller_id,
  });

  const isParts = product.category.toLowerCase().includes('part');

  return (
    <>
      <script
        id="schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectProductScript(product, schemaRatings) ?? '',
        }}
      />

      <script
        id="breadcrumbSchema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: injectBreadcrumbScript({ breadcrumbs }) ?? '',
        }}
      />

      <Suspense>
        <ShareProductModal product={product} />
      </Suspense>

      <div className="w-full bg-white">
        <div
          className={`w-full py-8 sm:mx-auto sm:max-w-default ${montserrat.className}`}
        >
          <div className="flex flex-col gap-y-4">
            <div className="w-full">
              <div className="hidden sm:mx-auto sm:flex sm:max-w-content">
                <Breadcrumb onProductDetails items={breadcrumbs} />
              </div>
            </div>

            {isParts && (
              <>
                {product.fitting_bikes?.universal ? (
                  <InformationBadge
                    text={`This part has been listed as universal fitment. Please verify compatibility with the seller.`}
                    type="info"
                  />
                ) : (
                  <>
                    {!product.fitting_bikes?.bikes ||
                    !product.fitting_bikes?.bikes.length ? (
                      <></>
                    ) : (
                      <div className="flex flex-col gap-12 p-4 sm:gap-4 sm:p-0">
                        {userDoc && !!userDoc.bikes.length && (
                          <FitmentInformationBadges product={product} />
                        )}

                        <ConfirmFitment product={product} />
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <ProductCouponDisplay product={product} />

            <ProductDetailsBanner product={product} />
            <div className="mx-auto flex w-full flex-col items-center justify-center gap-[0.8rem] lg:flex-row">
              {!!userDoc?.roles?.admin && (
                <ProductAdminControls product={product} />
              )}
              {!!userDoc?.roles?.admin || !!userDoc?.roles?.curated_lists ? (
                <AddToCuratedList product={product} />
              ) : null}
            </div>

            <div className="hidden w-full px-4 lg:block">
              {userDoc?.roles?.support && (
                <div className="flex w-full justify-between gap-[0.8rem] font-semibold">
                  <span>
                    <strong>Product id:</strong> {product.id}
                  </span>
                  <span>
                    <strong>Product created:</strong>{' '}
                    {formatTimestamp(product.created)}
                  </span>
                  {!!product.sold_date && (
                    <span>
                      <strong>Product sold:</strong>
                      {formatTimestamp(product.sold_date)}
                    </span>
                  )}
                  {typeof product.fee_override === 'number' && (
                    <span>
                      <strong>Fee override:</strong> {product.fee_override}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="hidden w-full sm:block">
              <SellerVacationDisplay product={product} />
            </div>
          </div>

          <div className="mt-8">
            <ProductDetails
              product={product}
              seller={seller}
              breadcrumbs={breadcrumbs}
              schemaRatings={schemaRatings}
              fitment={fitment}
              selectedVariations={selectedVariations}
              onSelection={onSelection}
              setErrors={setErrors}
            />

            <div className="mt-[10rem]">
              <ProductInfoTabs
                product={product}
                breadcrumbs={breadcrumbs}
                fitment={fitment}
                selectedVariations={selectedVariations}
              />
            </div>
          </div>
        </div>

        {seller && (
          <div className="w-full bg-[#F6F6F6] pb-8 pt-16">
            <div className="flex w-full sm:mx-auto sm:max-w-default">
              <ProductMeetTheSeller
                sellerDoc={seller}
                reviews={reviews ?? []}
                product_id={product.id}
              />
            </div>
          </div>
        )}

        <div>
          <div
            className={`w-full py-8 sm:mx-auto sm:max-w-default ${montserrat.className}`}
          >
            <div className="mt-16 flex">
              {!!relatedProducts?.length && (
                <div className="mt-16 w-[50rem] px-2 sm:w-[140rem] sm:text-left">
                  <PageSectionHeader
                    title="Similar Items"
                    titleClassName="!text-[2rem] sm:!text-[2.5rem] mb-8"
                  />

                  <div className="mt-4 pl-8 sm:pl-0">
                    <Carousel
                      basis="basis-1/3.5"
                      items={
                        relatedProducts.map((product) => (
                          <MXSmallProductCard
                            key={product.id}
                            product={product}
                          />
                        )) ?? []
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 flex">
              {!!basedOnActivity?.length && (
                <div className="mt-16 w-[50rem] px-2 sm:w-[140rem] sm:text-left">
                  <div className="flex w-full">
                    <PageSectionHeader
                      title="Inspired by your recent views"
                      titleClassName="!text-[2rem] sm:!text-[2.5rem] mb-8"
                    />
                  </div>

                  <div className="mt-4 pl-8 sm:pl-0">
                    <Carousel
                      items={
                        basedOnActivity.map((product) => (
                          <MXSmallProductCard
                            key={product.id}
                            product={product}
                          />
                        )) ?? []
                      }
                      basis={'basis-1/6.5'}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
